import { HubConnectionState } from "@microsoft/signalr";
import { createHub } from "@services/realtime/createHub";
import { nextRetryDelay } from "@services/realtime/retryPolicy";
import { createVisibilityGate } from "@services/realtime/visibilityGate";

// Long enough that switching between tabs causes no churn, short enough to let go before
// Chrome starts throttling the tab (after five minutes hidden).
const HIDDEN_GRACE_MS = 3 * 60 * 1000;

export const REALTIME_STATUS = Object.freeze({
  DISCONNECTED: "disconnected",
  CONNECTING: "connecting",
  CONNECTED: "connected",
  RECONNECTING: "reconnecting",
});

export function createRealtimeConnection(options = {}) {
  const { doc = globalThis.document, hiddenGraceMs = HIDDEN_GRACE_MS, ...hubOptions } = options;
  const hub = createHub(hubOptions);
  const statusListeners = new Set();
  const connectedCallbacks = new Set();
  let status = REALTIME_STATUS.DISCONNECTED;
  let active = false;
  let attempts = 0;
  let retryTimer = null;
  let queue = Promise.resolve();

  // Listeners are outside code (UI): one that throws must not abort the connect loop.
  const setStatus = (next) => {
    if (next === status) return;
    status = next;
    statusListeners.forEach((listener) => {
      try {
        listener(next);
      } catch (error) {
        console.error("[Realtime] Status listener failed:", error);
      }
    });
  };

  const runConnected = (callback) =>
    Promise.resolve()
      .then(callback)
      .catch((error) => console.error("[Realtime] onConnected callback failed:", error));

  // The server treats every (re)connect as a brand-new caller, so features redo their setup
  // here: re-join pages, re-claim locks, re-fetch what may have changed while offline.
  const announceConnected = () => {
    setStatus(REALTIME_STATUS.CONNECTED);
    connectedCallbacks.forEach(runConnected);
  };

  const shouldConnect = () => active && gate.isOpen();

  const cancelRetry = () => {
    clearTimeout(retryTimer);
    retryTimer = null;
  };

  const scheduleRetry = () => {
    cancelRetry();
    setStatus(REALTIME_STATUS.RECONNECTING);
    retryTimer = setTimeout(() => { retryTimer = null; sync(); }, nextRetryDelay(attempts));
    attempts += 1;
  };

  const connect = async () => {
    if (hub.state !== HubConnectionState.Disconnected) return;
    setStatus(attempts === 0 ? REALTIME_STATUS.CONNECTING : REALTIME_STATUS.RECONNECTING);
    try {
      await hub.start();
    } catch (error) {
      console.warn("[Realtime] Could not connect:", error);
      if (shouldConnect()) scheduleRetry();
      return;
    }
    attempts = 0;
    // Signed out (or hidden) while connecting: the stop already queued behind us takes over.
    if (shouldConnect()) announceConnected();
  };

  const disconnect = async () => {
    cancelRetry();
    attempts = 0;
    await hub.stop();
    setStatus(REALTIME_STATUS.DISCONNECTED);
  };

  // Every start/stop request goes through one queue, so a sign-out during a connect (or
  // React mounting twice in development) can never race two operations on the same hub.
  const sync = () => {
    queue = queue
      .then(() => (shouldConnect() ? connect() : disconnect()))
      .catch((error) => console.error("[Realtime] Connection update failed:", error));
    return queue;
  };

  const gate = createVisibilityGate({ doc, graceMs: hiddenGraceMs, onChange: sync });

  // Reached after our own stop(), when the line drops, and when the server ends the
  // connection. While signed in the tab comes back through the same retry as a failed start,
  // waiting longer after each failure and never giving up.
  hub.onclose(() => {
    if (shouldConnect()) scheduleRetry();
    else setStatus(REALTIME_STATUS.DISCONNECTED);
  });

  const onConnected = (callback) => {
    connectedCallbacks.add(callback);
    if (status === REALTIME_STATUS.CONNECTED) runConnected(callback);
    return () => connectedCallbacks.delete(callback);
  };

  const subscribe = (listener) => {
    statusListeners.add(listener);
    return () => statusListeners.delete(listener);
  };

  // The same hub object lives for the whole session, so handlers registered once stay
  // attached across reconnects and sign-in/sign-out cycles.
  const on = (event, handler) => {
    hub.on(event, handler);
    return () => hub.off(event, handler);
  };

  return {
    start: () => { active = true; return sync(); },
    stop: () => { active = false; return sync(); },
    on,
    invoke: (method, ...args) => hub.invoke(method, ...args),
    onConnected,
    // Keeps a background tab connected (unsaved changes, a held page lock). Returns release().
    holdOpen: gate.hold,
    getStatus: () => status,
    subscribe,
  };
}

// The one connection for this tab. Feature code reaches it through createRealtimeStore.
const realtimeConnection = createRealtimeConnection();

export default realtimeConnection;
